import Store from '@store';
import { buildLinkedTextId, splitLinkedTextAreaLink } from 'src/helpers/text-overflow-links';
import { logError } from 'src/utils/logger';

export class AdvancedOrderedListChainHandler {
  #store;

  constructor () {
    this.#store = Store;
  }

  #getOrderedListMetadata () {
    const state = this.#store.getState();
    const chains = state.metadata?.bullet_and_ordered_list?.list_styles;
    if (!chains) return null;

    return chains ?? null;
  }

  /**
  * Extract chain before specific page
  * @function advancedOrderedList:chainHandler.extractOrderedListChainsFromPreviousPage
  * @param {number} pageId - The current page ID e.g. 102 => we get then chain in page 100, 101
  * @returns {object} chains e.g. chains = {56: [100_svg-1, 100_svg-2, 101_svg-3], 101: [100_svg-1, 100_svg-2, 101_svg-3]}
  */
  extractChainsFromPreviousPages (pageId) {
    const chains = {};
    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return null;

    const stylesToRemove = [];
    for (const styleId in orderedListMetadata) {
      if (!orderedListMetadata[styleId].continue_across_stories) continue;

      const elements = orderedListMetadata[styleId].chains;
      chains[styleId] = [];
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        if (Number(element.page_id) === Number(pageId)) break;
        chains[styleId].push({
          pageId: element.page_id,
          id: element.element_id
        });

        if (i === elements.length - 1) stylesToRemove.push(styleId);
      }
    }

    for (const styleId of stylesToRemove) {
      delete chains[styleId];
    }

    return chains;
  }

  /**
  * Extract all chains
  * @function advancedOrderedList:chainHandler.extractChains
  * @returns {object} chains e.g. chains = {56: [103_svg-1, 103_svg-2, 104_svg-3], 101: [103_svg-1, 103_svg-2, 104_svg-3]}
  */
  extractChains () {
    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return {};

    const chains = {};
    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      chains[styleId] = [];
      for (const element of style.chains) {
        chains[styleId].push({
          pageId: element.page_id,
          id: element.element_id
        });
      }
    }

    return chains;
  }

  /**
  * Extract chain after specific page (exclusive)
  * @function advancedOrderedList:chainHandler.extractChainsFromNextPages
  * @param {number} pageId - The query page ID e.g. 102 => we get then chain in page 103, 104
  * @returns {object} chains e.g. chains = {56: [103_svg-1, 103_svg-2, 104_svg-3], 101: [103_svg-1, 103_svg-2, 104_svg-3]}
  */
  extractChainsFromNextPages (pageId) {
    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return null;

    const chains = {};
    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      chains[styleId] = [];
      let startAdding = false;
      for (const element of style.chains) {
        if (Number(element.page_id) === Number(pageId)) {
          // If the chain contains the query element, start adding next element
          startAdding = true;
          continue;
        }
        startAdding && chains[styleId].push({
          pageId: element.page_id,
          id: element.element_id
        });
      }

      if (!chains[styleId].length) {
        delete chains[styleId];
      }
    }

    return chains;
  }

  /**
  * Extract chain before specific element (exclusive the element)
  * @function advancedOrderedList:chainHandler.extractChainsBeforeElement
  * @param {number} pageId - The element's page ID
  * @param {number} elementId - The query element ID
  * @returns {object} chains e.g. chains = {56: [103_svg-1, 103_svg-2, 104_svg-3], 101: [103_svg-1, 103_svg-2, 104_svg-3]}
  */
  extractChainsBeforeElement (pageId, elementId) {
    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return null;

    const chains = {};
    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      chains[styleId] = [];
      let shouldKeep = false;

      for (const element of style.chains) {
        if (Number(element.page_id) === Number(pageId) && element.element_id === elementId) {
          // If the chain contains the query element, break and keep the chain
          shouldKeep = true;
          break;
        }

        chains[styleId].push({
          pageId: element.page_id,
          id: element.element_id
        });
      }

      if (!chains[styleId].length || !shouldKeep) {
        delete chains[styleId];
      }
    }

    return chains;
  }

  /**
  * Extract chain after specific element (exclusive the element)
  * @function advancedOrderedList:chainHandler.extractOrderedListChainsAfterElement
  * @param {number} pageId - The element's page ID
  * @param {number} elementId - The query element ID
  * @returns {object} chains e.g. chains = {56: [103_svg-1, 103_svg-2, 104_svg-3], 101: [103_svg-1, 103_svg-2, 104_svg-3]}
  */
  extractChainsAfterElement (pageId, elementId) {
    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return null;

    const chains = {};
    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      chains[styleId] = [];
      let startAdding = false;

      for (const element of style.chains) {
        if (Number(element.page_id) === Number(pageId) && element.element_id === elementId) {
          // If the chain contains the query element, start adding next element
          startAdding = true;
          continue;
        }
        startAdding && chains[styleId].push({
          pageId: element.page_id,
          id: element.element_id
        });
      }

      if (!chains[styleId].length) {
        delete chains[styleId];
      }
    }

    return chains;
  }

  /**
  * Extract ordered data (style, level, start from) from advanced ordered list chain
  * @function advancedOrderedList:chainHandler.extractOrderedDataFromChain
  * @param {number} orderedStyleId - The ordered list style ID
  * @param {number} chains - The advance ordered list chains {56: [103_svg-1, 103_svg-2, 104_svg-3], 101: [103_svg-1, 103_svg-2, 104_svg-3]}
  * @returns {object} chainOrderedData
  */
  extractOrderedDataFromChain (orderedStyleId, chains) {
    const chain = chains[orderedStyleId];
    const chainOrderedData = {
      style: [],
      level: [],
      startFrom: [],
      numberingStyle: [],
      regex: []
    };
    for (const element of chain) {
      const { pageId, id } = element;
      const elementOrderedData = this.getElementOrderedDataFromStore(pageId, id, orderedStyleId);

      for (const key of Object.keys(chainOrderedData)) {
        chainOrderedData[key] = chainOrderedData[key].concat(elementOrderedData[key]);
      }
    }

    return chainOrderedData;
  }

  /**
  * Filter chain by page ID. The function will modify input chains
  * @function advancedOrderedList:chainHandler.filterChainByPage
  * @param {object} chains - {56: [], 101: [], 301: []}
  * @param {number} pageId - The page ID
  * @returns {object} chains = {56: [], 101: [], 301: []}
  */
  filterChainByPage (chains, pageId) {
    for (const styleId in chains) {
      if (!chains[styleId]?.length) continue;
      chains[styleId] = chains[styleId].filter(element => Number(element.pageId) === Number(pageId));
    }

    return chains;
  }

  /**
  * Extract a segment of chain  in a specific page
  * @function advancedOrderedList:chainHandler.extractChainOnPage
  * @param {number} pageId - The page ID
  * @returns {object} chains = {56: [], 101: [], 301: []}
  */
  extractChainOnPage (pageId) {
    const orderedListMetadata = this.#getOrderedListMetadata();
    if (!orderedListMetadata) return null;

    const chains = {};
    for (const styleId in orderedListMetadata) {
      const style = orderedListMetadata[styleId];
      if (!style.continue_across_stories) continue;

      chains[styleId] = [];
      for (const element of style.chains) {
        if (Number(element.page_id) !== Number(pageId)) continue;
        chains[styleId].push({
          pageId: element.page_id,
          id: element.element_id
        });
      }

      if (!chains[styleId].length) {
        delete chains[styleId];
      }
    }

    return chains;
  }

  isElementInChain (chain, { pageId, elementId }) {
    return chain.some(item => item.id === elementId && Number(item.pageId) === pageId);
  }

  isPageInChain (chain, pageId) {
    return chain.some(item => Number(item.pageId) === Number(pageId));
  }

  getElementOrderedDataOnCurrentPage (elementId, currentPageId = INKIVE.Tool._current_design_id, isInclusive = false) {
    const chains = this.extractChainsBeforeElement(currentPageId, elementId);
    const currentPageChain = this.filterChainByPage(chains, currentPageId);
    const chainsArray = this.convertChainsToArray(currentPageChain);
    const textsLinkId = this.sortTextsToRender(chainsArray);

    const orderedData = {
      style: [],
      level: [],
      startFrom: [],
      numberingStyle: [],
      regex: []
    };

    for (const textLink of textsLinkId) {
      const [pageId, id] = splitLinkedTextAreaLink(textLink);
      const elementData = currentPageId === Number(pageId)
        ? this.getElementOrderedDataFromDOM(pageId, id)
        : this.getElementOrderedDataFromStore(pageId, id);

      orderedData.style = orderedData.style.concat(elementData.style);
      orderedData.level = orderedData.level.concat(elementData.level);
      orderedData.startFrom = orderedData.startFrom.concat(elementData.startFrom);
      orderedData.numberingStyle = orderedData.numberingStyle.concat(elementData.numberingStyle);
      orderedData.regex = orderedData.regex.concat(elementData.regex);
    }

    return orderedData;
  }

  sortTextsToRender (chainsArray) {
    const inDegree = {};
    const graph = {};

    for (const child of chainsArray.flat()) {
      inDegree[child] = 0;
      graph[child] = [];
    }

    for (const chain of chainsArray) {
      for (let i = 0; i < chain.length - 1; i++) {
        const parent = chain[i];
        const child = chain[i + 1];

        if (child !== parent) { // TODO: Remove this condition when metadata fixes duplicated items
          graph[parent].push(child);
          inDegree[child] += 1;
        }
      }
    }

    const queue = [];
    const result = [];
    for (const child in inDegree) {
      if (!inDegree[child]) queue.push(child);
    }

    while (queue.length) {
      const levelSize = queue.length;
      for (let i = 0; i < levelSize; i++) {
        const currentNode = queue.shift();
        const children = graph[currentNode];

        result.push(currentNode);
        for (const child of children) {
          inDegree[child] -= 1;
          if (inDegree[child] === 0) {
            queue.push(child);
          }
        }
      }
    }

    if (result.length !== Object.keys(inDegree).length) {
      logError('sortTextToRender')(new Error('Invalid chains. Graph has cycle.'));
      return [];
    }

    return result;
  }

  getElementsOrderedDataOnCurrentPage (pageId = INKIVE.Tool._current_design_id) {
    const chains = this.extractChainOnPage(pageId);
    const chainsArray = this.convertChainsToArray(chains);
    const textsLinkId = this.sortTextsToRender(chainsArray);

    const orderedData = {
      style: [],
      level: [],
      startFrom: [],
      numberingStyle: [],
      regex: []
    };

    for (const textLink of textsLinkId) {
      const [pageId, id] = splitLinkedTextAreaLink(textLink);
      const elementData = this.getElementOrderedDataFromDOM(pageId, id);
      orderedData.style = orderedData.style.concat(elementData.style);
      orderedData.level = orderedData.level.concat(elementData.level);
      orderedData.startFrom = orderedData.startFrom.concat(elementData.startFrom);
      orderedData.numberingStyle = orderedData.numberingStyle.concat(elementData.numberingStyle);
      orderedData.regex = orderedData.regex.concat(elementData.regex);
    }

    return orderedData;
  }

  getElementOrderedDataFromDOM (pageId = INKIVE.Tool._current_design_id, elementId) {
    const orderedData = {
      style: [], level: [], startFrom: [], numberingStyle: [], regex: []
    };
    const queryDocument = Number(pageId) === INKIVE.Tool._current_design_id
      ? document.getElementById('svgcontent')
      : document.getElementById('textmock');

    const textarea = Number(pageId) === INKIVE.Tool._current_design_id
      ? queryDocument.getElementById(elementId)
      : queryDocument.querySelector(`#${elementId}[data-page-id="${pageId}"]`);

    if (!textarea) return orderedData;
    // Select all text elements that are first line of the paragraph
    const headTexts = textarea.querySelectorAll('text:first-child, text.eol + text.eol, text.eol + text:not(.eol)');

    // TODO: We might need this
    // const isLastLineEol = textarea.querySelector('text:last-child')?.classList?.contains('eol');
    const hasOrderedList = !!textarea.querySelector('text[data-ordered]');
    if (!hasOrderedList || headTexts === null || !headTexts.length) return orderedData;

    headTexts.forEach(text => {
      const style = Number(text.dataset.orderedStyle) || 0;
      const level = Number(text.dataset.ordered) || 0;
      const startFrom = Number(text.dataset.orderedStart) || 0;
      const numberingStyle = text.dataset.orderedNumberingStyle || 0;
      const regex = text.dataset.orderedRegex || '';

      orderedData.style.push(style);
      orderedData.level.push(level);
      orderedData.startFrom.push(startFrom);
      orderedData.numberingStyle.push(numberingStyle);
      orderedData.regex.push(regex);
    });

    return orderedData;
  }

  /**
  * Extract ordered data (style, level, start from) from advanced ordered list chain
  * @function advancedOrderedList:chainHandler.getElementOrderedDataFromStore
  * @param {number} pageId - The ordered list style ID
  * @param {number} elementId - The ordered list style ID
  * @param {number} orderedStyleId - The ordered list style ID
  * @returns {object} chainOrderedData
  */
  getElementOrderedDataFromStore (pageId, elementId, orderedStyleId) {
    const { elements } = Store.getState();
    const elementData = elements[pageId]?.[elementId];
    const orderedData = {
      style: [],
      level: [],
      startFrom: [],
      numberingStyle: [],
      regex: []
    };

    if (!elementData?.orderedList) return orderedData;

    for (let i = 0; i < elementData.orderedList.style.length; i++) {
      if (orderedStyleId && elementData.orderedList.style[i] !== Number(orderedStyleId)) continue;

      orderedData.style.push(elementData.orderedList.style[i]);
      orderedData.level.push(elementData.orderedList.level[i]);
      orderedData.startFrom.push(elementData.orderedList.startFrom[i]);
      orderedData.numberingStyle.push(elementData.orderedList.numberingStyle[i]);
      orderedData.regex.push(elementData.orderedList.regex[i]);
    }

    return orderedData;
  }

  getElementNodeStringFromStore (pageId, elementId) {
    const { elements } = Store.getState();
    const elementData = elements[pageId]?.[elementId];
    if (!elementData?.nodeString) return;

    const nodeString = elementData.nodeString;
    if (nodeString?.includes('data-page-id')) return nodeString;

    return nodeString.replace('id=', `data-page-id=${pageId} id=`);
  }

  /**
  * Convert chains object to array of link text ID
  * @function advancedOrderedList:chainHandler.convertChainToArray
  * @param {object} chains - chains = {56: [], 101: [], 301: []}
  * @returns {array} chainsArray = [[101-svg_56, 102-svg_67],[],[]]
  */
  convertChainsToArray (chains) {
    const chainsArray = [];
    for (const styleId in chains) {
      const chain = chains[styleId];
      if (!chain.length) continue;

      chainsArray.push(chain.map(element => buildLinkedTextId(element.pageId, element.id)));
    }

    return chainsArray;
  }

  /**
  * Convert chains object to array of link text ID
  * @function advancedOrderedList:chainHandler.groupChainsByPageAsArray
  * @param {object} chains - chains = {56: [], 101: [], 301: []}
  * @returns {object} {101: [[],[],[]], 102: [[],[],[]]}
  */
  groupChainsByPage (chains) {
    const result = {};

    return result;
  }
}
